<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <div v-if="isMultiTechEnabled">
          <span class="font-medium">{{ techSectionLabel }}</span>
          (Field {{ techCountLabel }})
        </div>
        <div v-else><span class="font-medium">Tech</span> (Field Worker)</div>
        <Tooltip v-if="appointment.assignment_pinned">
          <template #trigger>
            <i class="fas fa-lock text-gray-500" data-test="pinned-indicator" />
          </template>
          <template #tooltip>
            <div class="max-w-xs">
              <b v-if="appointment.pinned_reason">Pinned Reason:</b>
              {{ appointment.pinned_reason || "Pinned" }}
            </div>
          </template>
        </Tooltip>
      </div>
      <template v-if="isMultiTechEnabled">
        <AppointmentTechnician
          :appointment="appointment"
          @refresh-appointment="emit('refresh-appointment')"
        />
      </template>
      <template v-else>
        <TechnicianTimeline
          v-if="appointment.technician"
          :appointment="appointment"
          :technician="appointment.technician"
          :expanded="appointment.status !== 'completed'"
        />
        <AppointmentTechnician
          v-else
          :appointment="appointment"
          @refresh-appointment="emit('refresh-appointment')"
        />
      </template>
    </div>
    <div v-if="appointment.crew_members.length" class="flex flex-col gap-3">
      <div class="font-medium">Crew Members</div>
      <div
        v-for="crewMember in appointment.crew_members"
        :key="crewMember.id"
        class="flex flex-col gap-3"
      >
        <CrewMemberTimeline
          :appointment="appointment"
          :crew-member="crewMember"
          :expanded="false"
        />
      </div>
    </div>
    <div class="font-medium">Totals</div>
    <div class="bg-white rounded-lg p-4 border flex gap-3 *:flex-1">
      <div v-if="preWorkEnabled">
        <div class="text-xs text-gray-500">Pre-Work</div>
        <div>
          <MinutesLabel :minutes="appointment.total_pre_work_minutes" />
        </div>
      </div>
      <div>
        <div class="text-xs text-gray-500">Drive Time</div>
        <div><MinutesLabel :minutes="appointment.total_travel_minutes" /></div>
      </div>
      <div>
        <div class="text-xs text-gray-500">Work Duration</div>
        <div><MinutesLabel :minutes="appointment.total_work_minutes" /></div>
      </div>
      <div>
        <div class="text-xs text-gray-500">Total Duration</div>
        <div>
          <MinutesLabel :minutes="appointment.total_duration_minutes" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import MinutesLabel from "@/components/MinutesLabel.vue";
import Tooltip from "@/components/Tooltip.vue";
import { AppointmentV2 } from "@/typings/appointment";
import AppointmentTechnician from "./AppointmentTechnician.vue";
import CrewMemberTimeline from "./CrewMemberTimeline.vue";
import TechnicianTimeline from "./TechnicianTimeline.vue";

const emit = defineEmits(["refresh-appointment"]);
const props = defineProps<{ appointment: AppointmentV2 }>();
const isMultiTechEnabled = useFeatureFlag("multiple_technicians_ff_enabled");
const store = useStore();
const preWorkEnabled = computed(() =>
  store.getters.settingEnabledForTenant("pre_work_enabled")
);

const techCount = computed(() => {
  const atCount = props.appointment.appointment_technicians?.length ?? 0;
  if (atCount > 0) return atCount;
  return props.appointment.technician?.technician_id ? 1 : 0;
});

const techSectionLabel = computed(() =>
  techCount.value > 1 ? "Technicians" : "Tech"
);

const techCountLabel = computed(() =>
  techCount.value > 1 ? "Workers" : "Worker"
);
</script>

<style scoped></style>
